import React, { createContext, useContext, useCallback } from 'react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useToast } from '../hooks/use-toast';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const { isOnline, isOffline } = useNetworkStatus();
  const { toast } = useToast();

  /**
   * Check if action is allowed (blocks create/edit when offline)
   * Shows toast notification if blocked
   * @param {string} action - 'create', 'edit', 'delete'
   * @returns {boolean} - true if action is allowed
   */
  const checkOfflineAction = useCallback((action = 'edit') => {
    if (isOffline) {
      const actionText = {
        create: 'Creating new items',
        edit: 'Editing',
        delete: 'Deleting'
      }[action] || 'This action';

      toast({
        title: "You're offline",
        description: `${actionText} is disabled in offline mode. Please connect to the internet to make changes.`,
        variant: "warning",
        duration: 4000
      });
      return false;
    }
    return true;
  }, [isOffline, toast]);

  /**
   * Wrapper for actions that should be blocked when offline
   * @param {Function} action - Function to execute if online
   * @param {string} actionType - 'create', 'edit', 'delete'
   */
  const withOnlineCheck = useCallback((action, actionType = 'edit') => {
    return (...args) => {
      if (checkOfflineAction(actionType)) {
        return action(...args);
      }
      return null;
    };
  }, [checkOfflineAction]);

  /**
   * Show a simple offline notification
   */
  const showOfflineNotification = useCallback(() => {
    if (isOffline) {
      toast({
        title: "Device Offline",
        description: "You're viewing cached data. Some features may be limited.",
        variant: "warning",
        duration: 3000
      });
    }
  }, [isOffline, toast]);

  const value = {
    isOnline,
    isOffline,
    checkOfflineAction,
    withOnlineCheck,
    showOfflineNotification
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
